import { useCallback, useState } from 'react';

import { businessTypeOptions } from '@futrli/shared-services';

export interface AdditionalInfoFormInitialState {
  businessPhoneNumber: string;
  businessType: string;
  readPrivacy: boolean;
  receiveMarketingEmails: boolean | null;
  selectedCountry: string | null;
}

const MIN_PHONE_NUMBER_LENGTH = 10;

export const useAdditionalInfoForm = (initialValues: Partial<AdditionalInfoFormInitialState> = {}) => {
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState(initialValues.businessPhoneNumber || '');
  const [businessType, setBusinessType] = useState(initialValues.businessType || businessTypeOptions[0].id);
  const [readPrivacy, setReadPrivacy] = useState(!!initialValues.readPrivacy);
  const [receiveMarketingEmails, setReceiveMarketingEmails] = useState<boolean | null>(
    initialValues.receiveMarketingEmails ?? null
  );
  const [selectedCountry, setSelectedCountry] = useState<string | null>(initialValues.selectedCountry || null);

  const [countryTouched, setCountryTouched] = useState(false);
  const [phoneNumberTouched, setPhoneNumberTouched] = useState(false);

  const isCanadaOrUs = selectedCountry === 'Canada' || selectedCountry === 'United States';
  const isValid =
    readPrivacy && receiveMarketingEmails !== null && businessPhoneNumber.length >= MIN_PHONE_NUMBER_LENGTH;

  const handlePhoneNumberChange = useCallback((newPhoneNumber: string) => {
    setPhoneNumberTouched(true);
    setBusinessPhoneNumber(newPhoneNumber);
  }, []);

  const handleBusinessTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setBusinessType(e.target.value);
  }, []);

  const handleCountryChange = useCallback((country: string | null) => {
    setCountryTouched(true);
    setSelectedCountry(country);
  }, []);

  const handleReadPrivacyClick = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>) => {
      setReadPrivacy(!readPrivacy);
    },
    [readPrivacy]
  );

  const handleReceiveMarketingEmailsClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (
        (event.target.value === 'true' && receiveMarketingEmails) ||
        (event.target.value === 'false' && receiveMarketingEmails === false)
      ) {
        setReceiveMarketingEmails(null);
      } else {
        setReceiveMarketingEmails(event.target.value === 'true');
      }
    },
    [receiveMarketingEmails]
  );

  return {
    businessPhoneNumber,
    businessType,
    countryTouched,
    handleBusinessTypeChange,
    handleCountryChange,
    handlePhoneNumberChange,
    handleReadPrivacyClick,
    handleReceiveMarketingEmailsClick,
    isCanadaOrUs,
    isValid,
    phoneNumberTouched,
    readPrivacy,
    receiveMarketingEmails,
    selectedCountry
  };
};
